import { createTheme, type Theme } from '@mui/material/styles';
// Augments MUI's `Components` type with the MUI-X slots (MuiPickersPopper,
// MuiTreeItem, …) so they can be set in `theme.components` below.
import type { } from '@mui/x-date-pickers/themeAugmentation';
import type { } from '@mui/x-tree-view/themeAugmentation';

import darkVars from '@/assets/themes/dark-theme-viewnexa/_variables.module.scss';
import lightVars from '@/assets/themes/light-theme-viewnexa/_variables.module.scss';
import { DEFAULT_THEME_NAME } from '@/themes';

// Foundation MUI theme: palette, typography, and component styleOverrides,
// sourced from each theme's SCSS `:export` block. The legacy
// _vendor-overrides/mui.scss override sheet has been removed; component styling
// now lives here in `components`. The few remaining non-slot globals (e.g. the
// `.MuiDialog-root.viewnexa` variant) live in _vendor-overrides/_mui-globals.scss.
const buildTheme = (vars: Record<string, string>, mode: 'light' | 'dark'): Theme => {
  // Hot tokens reused across many slots, aliased once for brevity.
  const text = vars.baseTextColor;
  const bg = vars.bodyBg;
  const border = vars.tableTheadBorderColor;
  // `:export` values are strings (e.g. "14px"); MUI expects a number here.
  const fs = parseInt(vars.fontSizeBase, 10);

  return createTheme({
    palette: {
      mode,
      primary: { main: vars.brandPrimary },
      secondary: { main: vars.brandSecondary },
      success: { main: vars.brandSuccess },
      // info: { main: vars.brandInfos },
      warning: { main: vars.brandWarning },
      error: { main: vars.brandDanger },
      background: { default: bg, paper: bg },
      // secondary mirrors primary so label-ish slots (FormControlLabel, FormLabel)
      // that default to text.secondary don't fall back to MUI's near-black; this
      // matches the legacy `body { color: $base-text-color }` cascade.
      text: { primary: text, secondary: text, disabled: vars.disabledTextColor },
      divider: vars.dividerBorderColor,
    },
    typography: {
      fontFamily: vars.baseFont,
      fontSize: fs,
    },
    shape: { borderRadius: parseInt(vars.borderRadius, 10) },
    components: {
      // Generic base text color for every button-based element (menu items,
      // tabs, list items, plain text buttons). More specific slots below
      // (MuiButton, MuiIconButton, …) refine this.
      MuiButtonBase: {
        styleOverrides: {
          root: { color: text },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { color: vars.brandPrimary },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiButton: {
        defaultProps: {
          color: 'inherit',
        },
        styleOverrides: {
          contained: {
            fontSize: vars.buttonFontSize,
            borderRadius: vars.buttonBorderRadius,
            lineHeight: 'normal',
            '&:hover': {
              backgroundColor: vars.buttonDefaultBackgroundHover,
            },
            '&.Mui-disabled': {
              color: '#555',
              WebkitTextFillColor: '#555',
              backgroundColor: vars.disabledTextColor,
            },
          },
          containedInherit: {
            color: vars.buttonTextColor,
            backgroundColor: vars.buttonDefaultBackground,
          },
          containedPrimary: {
            '&:hover': {
              backgroundColor: vars.brandPrimaryHover,
            },
          },
          outlined: {
            fontSize: vars.buttonFontSize,
            color: text,
            borderRadius: vars.buttonBorderRadius,
            border: `1px solid ${vars.defaultBorderColor}`,
          },
          textPrimary: {
            color: text,
            fontSize: '16px',
            fontWeight: 400,
            textTransform: 'none',
          },
          // Default color is 'inherit', so text buttons land on the
          // textInherit/colorInherit variants where MUI forces `color: inherit`.
          // Pin them to the base text color so they render aec0d9 in the dark theme.
          textInherit: {
            color: text,
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: vars.controlBorderColor,
            '& + .MuiFormControlLabel-label': {
              color: vars.disabledTextColor,
            },
            '&.Mui-checked': {
              color: vars.brandPrimary,
              '& + .MuiFormControlLabel-label': {
                color: text,
              },
            },
            '&:hover': {
              backgroundColor: vars.controlHoverColor,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            width: 30,
            height: 30,
            marginBottom: 5,
            marginLeft: 5,
            color: vars.controlBorderColor,
            '&.Mui-checked': {
              color: vars.brandPrimary,
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: bg,
            color: text,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiSvgIcon-root': { fill: vars.brandPrimary },
            '& .MuiChip-deleteIcon': {
              fontSize: 17,
              fill: '#fff',
              stroke: 'transparent',
            },
          },
          option: {
            '&.Mui-focused': {
              backgroundColor: vars.controlSelectionColor,
              '&:hover': { backgroundColor: vars.controlHoverColor },
            },
          },
          noOptions: { color: text },
          paper: {
            backgroundColor: `${bg} !important`,
            color: text,
            border: `1px solid ${border}`,
            '& .MuiSvgIcon-root': {
              fill: vars.brandPrimary,
              stroke: vars.brandPrimary,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            letterSpacing: 'normal',
            fontSize: fs,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: vars.dividerBorderColor },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: bg,
            color: text,
            // Mirror the legacy `.MuiDialog-root .MuiPaper-root` descendant rule
            // so nested Paper surfaces inside a dialog inherit the theme bg/color.
            '& .MuiPaper-root:not(.MuiAlert-root)': {
              background: bg,
              color: text,
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.25rem',
            lineHeight: 'normal',
            padding: '9px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            lineHeight: 'normal',
            padding: '15px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            lineHeight: 'normal',
            padding: '15px 15px 20px 15px',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          label: { color: '#fff' },
          root: {
            backgroundColor: 'rgba(151, 114, 114, 0.466)',
            '&.MuiChip-colorPrimary': { backgroundColor: vars.brandPrimary },
            '&.MuiChip-colorSuccess': { backgroundColor: vars.brandSuccess },
            '&.MuiChip-colorSecondary': { backgroundColor: vars.tableHeadBackground },
            '&.MuiChip-colorWarning': { backgroundColor: vars.brandWarning },
            '&.MuiChip-colorError': { backgroundColor: vars.brandDanger },
            '&.MuiChip-colorInfo': { backgroundColor: 'rgba(92, 64, 216, 0.75)' },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          filled: { color: '#fff' },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: `${fs}px !important`,
            color: text,
            borderColor: `${border} !important`,
            '& fieldset.MuiOutlinedInput-notchedOutline': {
              borderColor: `${border} !important`,
            },
            '&.Mui-error fieldset.MuiOutlinedInput-notchedOutline': {
              borderColor: `${vars.brandDanger} !important`,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: { color: `${vars.brandPrimary} !important` },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            height: '22px !important',
            width: '35px !important',
          },
          switchBase: {
            '&.Mui-checked .MuiSwitch-track': {
              backgroundColor: `${vars.switchTrackColor} !important`,
              opacity: '1 !important',
            },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: `${vars.switchTrackColor} !important`,
              opacity: '1 !important',
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              backgroundColor: `${vars.switchTrackColor} !important`,
              opacity: 0.5,
            },
          },
          thumb: {
            height: '12px !important',
            width: '12px !important',
            position: 'relative',
            top: 1,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: bg,
            backgroundImage: 'none',
            color: text,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: { color: text },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: { borderColor: vars.dividerBorderColor },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          // `color` is pinned because the label otherwise inherits from the
          // surrounding container (e.g. `.vn-table { color: #fff }`), which made
          // checkbox/radio labels render white instead of the base text color.
          label: { fontSize: fs, color: text },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            width: '100%',
            color: text,
            backgroundColor: bg,
            '& .MuiFormLabel-root.MuiInputLabel-root': {
              fontSize: fs,
              color: text,
              backgroundColor: bg,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          formControl: {
            fontSize: fs,
            color: text,
            backgroundColor: bg,
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            marginBottom: 0,
            height: 30,
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            color: text,
            borderColor: border,
            '&.Mui-selected': {
              background: `${vars.brandPrimary} !important`,
              color: '#fff',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundColor: vars.brandPrimary },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: 50 },
        },
      },
      MuiBadge: {
        styleOverrides: {
          colorSuccess: { background: vars.brandPrimary },
          colorSecondary: { background: vars.tableHeadBackground },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            alignItems: 'flex-start',
            '&.Mui-selected': { color: vars.brandPrimary },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 1,
            color: text,
            '&.Mui-error': { color: vars.brandDanger },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: { color: vars.brandPrimary },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            color: 'unset',
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { backgroundColor: bg },
        },
      },
      // --- MUI-X: date pickers (via @mui/x-date-pickers themeAugmentation) ---
      MuiPickersPopper: {
        styleOverrides: {
          paper: {
            backgroundColor: bg,
            backgroundImage: 'none',
          },
          root: {
            backgroundColor: bg,
            color: text,
            '& .MuiDayPicker-weekDayLabel': { color: text },
            '& .MuiButtonBase-root.MuiPickersDay-root': {
              backgroundColor: bg,
              '&.MuiPickersDay-today': { backgroundColor: vars.controlSelectionColor },
              '&.Mui-selected': { backgroundColor: vars.brandPrimary },
              '&:hover': { backgroundColor: vars.controlHoverColor },
            },
            '& .MuiClockNumber-root': { color: text },
          },
        },
      },
      // --- MUI-X: tree view (via @mui/x-tree-view themeAugmentation) ---
      MuiTreeItem: {
        styleOverrides: {
          root: {
            color: text,
            padding: 3,
            '&:hover': { background: 'transparent' },
            '& > .MuiTreeItem-content > .MuiTreeItem-label': { width: '90%' },
            '& > .MuiTreeItem-content:hover': { background: 'transparent' },
            '& > .MuiTreeItem-content:hover > .MuiTreeItem-label': { background: 'transparent' },
            '& > .MuiTreeItem-content.Mui-selected': { background: 'transparent' },
            '& > .MuiTreeItem-content.Mui-selected:hover': { background: 'transparent' },
            '& > .MuiTreeItem-content.Mui-selected > .MuiTreeItem-label': {
              background: 'transparent',
              color: vars.brandPrimary,
            },
            '& > .MuiTreeItem-content.Mui-selected.Mui-focused': {
              backgroundColor: vars.controlSelectionColor,
            },
          },
        },
      },
    },
  });
};

const muiThemes: Record<string, Theme> = {
  'light-theme-viewnexa': buildTheme(lightVars, 'light'),
  'dark-theme-viewnexa': buildTheme(darkVars, 'dark'),
};

const getMuiTheme = (name: string): Theme => muiThemes[name] ?? muiThemes[DEFAULT_THEME_NAME];

export { getMuiTheme, muiThemes };
